import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import LastScanPanel from "../../components/warehouse/LastScanPanel";
import ScannedCodesPanel from "../../components/warehouse/ScannedCodesPanel";
import ScannerInput from "../../components/warehouse/ScannerInput";
import ScanInConfirmModal from "../../components/warehouse/ScanInConfirmModal";
import ScanResultTable from "../../components/warehouse/ScanResultTable";
import ScanStats from "../../components/warehouse/ScanStats";
import { buildStats } from "../../components/warehouse/scanResults";
import useWarehouseScan from "../../components/warehouse/useWarehouseScan";
import { fetchWarehousePermissions, WAREHOUSE_UNCLAIMED_FEATURE } from "../../libs/customerCreatePermission";
import "../../components/warehouse/warehouse.css";

/**
 * KHO VẬN → Quét nhập kho VN (BRD §4). Quét/paste chỉ kiểm tra; bấm Xác nhận mới chuyển Kho VN.
 */
const ScanInPage = () => {
  const scan = useWarehouseScan("IN", { autoOpen: true });
  // Gán Mã KH lúc xác nhận: quyền theo chức năng Đơn vô chủ (tạo = tạo đơn cho mã chưa tồn tại, sửa = gán KH đơn ZTO-VH00).
  const [unclaimedPerm, setUnclaimedPerm] = useState({ canCreate: false, canEdit: false });
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchWarehousePermissions(axiosInstance, WAREHOUSE_UNCLAIMED_FEATURE).then((perm) => {
      if (!cancelled) setUnclaimedPerm(perm);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Phiên chỉ có mã chưa tồn tại vẫn mở được modal xác nhận để tạo đơn (danh sách chính xác lấy từ server).
  const notFoundCount = useMemo(() => {
    if (!unclaimedPerm.canCreate) return 0;
    // recent: mới nhất ở trên — chỉ tính mã mà lần gần nhất vẫn là "chưa tồn tại" (chưa tạo đơn).
    const latest = new Map();
    scan.recent.forEach((log) => {
      if (log.code && !latest.has(log.code)) latest.set(log.code, log.result);
    });
    return [...latest.values()].filter((result) => result === "NOT_FOUND").length;
  }, [scan.recent, unclaimedPerm.canCreate]);

  const handleConfirm = () => setConfirmOpen(true);

  const submitConfirm = async (payload) => {
    const data = await scan.confirmAll(payload);
    if (data) setConfirmOpen(false);
  };

  const restartSession = async () => {
    const pendingCount = scan.pendingLogs.length;
    const confirm = await Swal.fire({
      title: "Kết thúc phiên quét nhập?",
      text:
        pendingCount > 0
          ? `Còn ${pendingCount} mã CHƯA xác nhận — các mã này sẽ bị bỏ, đơn không đổi trạng thái.`
          : "Thống kê sẽ bắt đầu lại từ 0 cho phiên mới.",
      icon: pendingCount > 0 ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: "Kết thúc & mở phiên mới",
      cancelButtonText: "Hủy",
    });
    if (!confirm.isConfirmed) return;
    await scan.closeSession();
    await scan.openSession();
  };

  return (
    <AdLayout>
      <div className="warehouse-page">
        <div className="warehouse-page__header">
          <h1 className="warehouse-page__title">Quét nhập kho VN</h1>
          <div className="d-flex gap-2">
            <Link className="btn btn-outline-secondary btn-sm" to="/kho-van/don-vo-chu">
              Mã chưa tồn tại / Đơn vô chủ
            </Link>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={restartSession} disabled={!scan.session}>
              Kết thúc phiên
            </button>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-lg-7">
            <ScannerInput
              enabled={!!scan.session}
              onScan={scan.enqueue}
              disabledPlaceholder={scan.loading ? "Đang mở phiên quét..." : "Chưa có phiên quét"}
            />
            <div className="mt-3">
              <ScanStats items={buildStats("IN", scan.counts, scan.total, scan.confirmedCount)} />
            </div>
            <div className="mt-3">
              <LastScanPanel result={scan.lastResult} pending={scan.pending} />
            </div>
          </div>
          <div className="col-lg-5">
            <ScannedCodesPanel
              pendingLogs={scan.pendingLogs}
              confirmedCodes={scan.confirmedCodes}
              confirmLabel="Xác nhận nhập kho"
              onConfirm={handleConfirm}
              onRemove={scan.removePending}
              confirmDisabled={!scan.session || scan.pending > 0}
              confirming={scan.confirming}
              confirmCount={scan.pendingLogs.length + notFoundCount}
            />
          </div>
        </div>

        <h2 className="h6 fw-bold">Kết quả quét gần nhất</h2>
        <ScanResultTable rows={scan.recent} />

        <ScanInConfirmModal
          show={confirmOpen}
          onHide={() => setConfirmOpen(false)}
          sessionId={scan.session?.id}
          pendingLogs={scan.pendingLogs}
          canCreate={unclaimedPerm.canCreate}
          canEdit={unclaimedPerm.canEdit}
          confirming={scan.confirming}
          onConfirm={submitConfirm}
        />
      </div>
    </AdLayout>
  );
};

export default ScanInPage;
